import type { TargetedWheelEvent } from "preact";
import { useRef } from "preact/hooks";
import type { PictureConfiguration } from "@brickme/project-core/src/model/picture.ts";
import { maxImageZoom } from "@brickme/project-core/src/model/picture.ts";
import roundAndConstrainImageZoomOffset from "@brickme/project-core/src/utils/round-and-constrain-image-zoom-offset.ts";
import type { GenericSize } from "@brickme/project-core/src/model/geom.ts";
import { usePicture } from "./picture-edit-context.tsx";
import { useRenderMode } from "../picture-render/render-mode-context.tsx";

function getImageZoomOffsetFree(
	{ width: sourceWidth, height: sourceHeight }: GenericSize<number>,
	{
		imageZoom,
		basePlateSize,
		numberOfBasePlates,
	}: Pick<
		PictureConfiguration,
		"imageZoom" | "basePlateSize" | "numberOfBasePlates"
	>,
) {
	const numberOfBricks = {
		width: numberOfBasePlates.width * basePlateSize,
		height: numberOfBasePlates.height * basePlateSize,
	};
	const xScale = numberOfBricks.width / sourceWidth;
	const yScale = numberOfBricks.height / sourceHeight;
	const useScale = Math.max(xScale, yScale);
	const cropWidth = numberOfBricks.width / useScale / imageZoom;
	const cropHeight = numberOfBricks.height / useScale / imageZoom;
	const zoomFreeX = sourceWidth - cropWidth;
	const zoomFreeY = sourceHeight - cropHeight;
	const bricksPerPixel = numberOfBricks.width / cropWidth;
	return {
		widthDimensionMax: Math.round((zoomFreeX * bricksPerPixel) / 2),
		heightDimensionMax: Math.round((zoomFreeY * bricksPerPixel) / 2),
	};
}

const imageZoomStep = 0.02;

const wheelImageZoomSourceRenderId = "wheel-image-zoom";

type MouseWheelImageZoomOptions = {
	readonly sourceImageDimensions: GenericSize<number>;
};

function useMouseWheelImageZoom({
	sourceImageDimensions,
}: MouseWheelImageZoomOptions) {
	const { patchPicture, picture } = usePicture();
	const { imageZoom, imageZoomOffset } = picture;

	const { requestRenderMode, withdrawRenderModeRequest } = useRenderMode();

	const zoomFinishRenderTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

	function onWheel(e: TargetedWheelEvent<HTMLCanvasElement>) {
		if (e.deltaY === 0) {
			return;
		}

		// We try and make the zoom in and out happen where the focus of the image is. i.e.
		// if there's no image zoom offset then changing the zoom alone is enough. If there
		// is an image zoom offset, then we want to change the offset to be proportional
		const direction = e.deltaY / Math.abs(e.deltaY);
		const newImageZoom = Math.min(
			maxImageZoom,
			Math.max(1, imageZoom + direction * imageZoomStep),
		);

		// Note that patchPicture has some clamping of zoom offsets, so don't need to do that
		// work here (to make sure zoom image offsets don't go outside of bounds).
		const oldZoomFreeBricks = getImageZoomOffsetFree(
			sourceImageDimensions,
			picture,
		);
		const oldOffsetRatios = {
			x:
				oldZoomFreeBricks.widthDimensionMax === 0
					? 0
					: imageZoomOffset.x / oldZoomFreeBricks.widthDimensionMax,
			y:
				oldZoomFreeBricks.heightDimensionMax === 0
					? 0
					: imageZoomOffset.y / oldZoomFreeBricks.heightDimensionMax,
		};
		const newZoomFreeBricks = getImageZoomOffsetFree(sourceImageDimensions, {
			numberOfBasePlates: picture.numberOfBasePlates,
			basePlateSize: picture.basePlateSize,
			imageZoom: newImageZoom,
		});
		const newImageZoomOffsets = {
			x: oldOffsetRatios.x * newZoomFreeBricks.widthDimensionMax,
			y: oldOffsetRatios.y * newZoomFreeBricks.heightDimensionMax,
		};
		patchPicture({
			imageZoom: newImageZoom,
			imageZoomOffset: roundAndConstrainImageZoomOffset(
				sourceImageDimensions,
				picture,
				newImageZoomOffsets,
			),
		});

		// Mouse wheel comes through as discrete events. We want to show source image "while"
		// zooming with the mouse. So we use 300ms buffer while zooming with wheel.
		requestRenderMode(wheelImageZoomSourceRenderId, "source");
		const { current: previousWithdrawTimeoutId } = zoomFinishRenderTimeoutRef;
		if (previousWithdrawTimeoutId !== undefined) {
			clearTimeout(previousWithdrawTimeoutId);
		}
		zoomFinishRenderTimeoutRef.current = setTimeout(() => {
			withdrawRenderModeRequest(wheelImageZoomSourceRenderId);
			zoomFinishRenderTimeoutRef.current = undefined;
		}, 300);
	}

	return { onWheel };
}

export default useMouseWheelImageZoom;
